import { Articolo } from '../_models/Articolo';
import { ArticoloVarianteLocale } from '../_models/ArticoloVarianteLocale';
import { CampoPropagabile, ModificaCampo, modificheDi } from './propagazione-comune';
import { AnteprimaImpattoOrdiniArticolo, ImpattoConteggioArticolo } from '../_services/articoli.service';

// Feature 121 (issue #3372) — diff fra lo stato di apertura della scheda articolo e quello alla
// conferma, per proporre la propagazione alle righe degli ordini clienti campo per campo.
//
// Helper PURO: nessuna dipendenza Angular, nessuna chiamata HTTP, nessuno stato. È il punto in cui
// si decide COSA mostrare nel dialogo e QUANTE righe promettere; i VALORI propagati non passano di
// qui — il backend li rilegge dal proprio database (spec FR-030), e questi servono solo a mostrare
// "vecchio → nuovo" all'operatore.
//
// ── Mappa dei campi propagabili: TERZA delle tre copie ─────────────────────────────────────────
// 1) BackEnd/ArchiConnectSaleApi/src/.../Sale/Helpers/PropagazioneCampiOdV.cs (whitelist)
// 2) BackEnd/ArchiConnectProductApi/src/.../Product/Services/ArticoloPropagazioneService.cs
//    (risoluzione dei valori dal database)
// 3) questo file (etichette e diff per il dialogo)
// Le `chiave` sono i nomi delle COLONNE DI DESTINAZIONE su OrdineVendita: è ciò che permette a
// Product e Sale di validarle senza tradurre nulla. Chi ne cambia una qui deve allinearle tutte.

// Le tre sezioni del dialogo (spec FR-005). "Variante" è una sezione a sé e non un campo dei dati
// articolo, perché il suo predicato di applicazione è diverso (research D6).
export type SezionePropagazione = 'articolo' | 'udm' | 'variante';

export interface ModificaCampoArticolo extends ModificaCampo {
  sezione: SezionePropagazione;
  gruppo?: 'udm';
}

export interface DiffPropagazioneArticolo {
  articolo: ModificaCampoArticolo[];
}

export interface DiffPropagazioneVariante {
  varianteId: number;
  modifiche: ModificaCampoArticolo[];
}

// Definizione dei cinque campi propagabili, con sezione, gruppo e default di selezione.
// ⚠️ `UdM` e `MagUdM` condividono il gruppo `udm` e la stessa sorgente (Articolo.umMag): una sola
// casella li governa entrambi (spec FR-006). Le tre voci della sezione unità di misura sono
// DESELEZIONATE di default (spec FR-008, research D8): cambiare l'unità di misura senza
// riconvertire le quantità altera il significato del dato, e va reso possibile solo come scelta
// consapevole.
interface DefinizioneCampoArticolo extends CampoPropagabile<Articolo, 'udm'> {
  sezione: SezionePropagazione;
  selezionatoDiDefault: boolean;
}

export const CAMPI_ARTICOLO: DefinizioneCampoArticolo[] = [
  {
    chiave: 'CodiceArticolo', etichetta: 'Codice articolo', sezione: 'articolo',
    selezionatoDiDefault: true, getter: a => a.codArt,
  },
  {
    chiave: 'VarianteArt', etichetta: 'Variante', sezione: 'variante',
    selezionatoDiDefault: true, getter: a => a.varianteArt,
  },
  {
    chiave: 'UdM', etichetta: 'Unità di misura', sezione: 'udm', gruppo: 'udm',
    selezionatoDiDefault: false, getter: a => a.umMag,
  },
  {
    chiave: 'MagUdM', etichetta: 'UdM magazzino', sezione: 'udm', gruppo: 'udm',
    selezionatoDiDefault: false, getter: a => a.umMag,
  },
  {
    chiave: 'UdM2', etichetta: 'Unità di misura 2', sezione: 'udm',
    selezionatoDiDefault: false, getter: a => a.umMag2,
  },
];

// Nel blocco VARIANTE l'unica voce è il codice variante: la descrizione non ha colonna di
// destinazione su OrdineVendita (spec FR-025, FR-052).
export const CAMPI_VARIANTE: CampoPropagabile<ArticoloVarianteLocale>[] = [
  { chiave: 'VarianteArt', etichetta: 'Variante', getter: v => v.varianteArt },
];

// ⚠️ `UdM` e `MagUdM` hanno lo STESSO getter: se cambia l'unità di misura di magazzino, `modificheDi`
// produce due voci identiche. Il dialogo ne mostra UNA sola casella (il gruppo `udm`), ma nella
// richiesta finiscono entrambe le chiavi — ed è quello che il predicato server-side si aspetta.
export function calcolaDiffArticolo(prima: Articolo, dopo: Articolo): DiffPropagazioneArticolo {
  const modifiche = CAMPI_ARTICOLO.flatMap(campo => {
    const [modifica] = modificheDi([campo], prima, dopo, campo.selezionatoDiDefault);
    return modifica ? [{ ...modifica, sezione: campo.sezione, gruppo: campo.gruppo }] : [];
  });
  return { articolo: modifiche };
}

export function calcolaDiffVariante(
  prima: ArticoloVarianteLocale, dopo: ArticoloVarianteLocale,
): DiffPropagazioneVariante {
  return {
    varianteId: dopo.varianteId,
    modifiche: modificheDi(CAMPI_VARIANTE, prima, dopo)
      .map(m => ({ ...m, sezione: 'variante' as SezionePropagazione })),
  };
}

// Prima delle due condizioni che aprono il dialogo (spec FR-002): senza modifiche propagabili non
// si chiede nulla. La seconda è il conteggio di riferimento (FR-016), che vive nel componente.
export function haModifichePropagabiliArticolo(diff: DiffPropagazioneArticolo): boolean {
  return diff.articolo.length > 0;
}

export function haModifichePropagabiliVariante(diff: DiffPropagazioneVariante): boolean {
  return diff.modifiche.length > 0;
}

// ── Il numero mostrato (spec FR-009, FR-010, data-model §4) ────────────────────────────────────

export type AmbitoPropagazione = 'inCorso' | 'totali';

// Chi chiede il numero. È l'UNICO parametro che introduce una divergenza, e serve al solo caso
// "nessun campo spuntato": il pulsante *promette un risultato* e senza campi non ha nulla da
// promettere; le due etichette d'ambito *servono a scegliere l'ambito* e mostrare `0` su entrambe
// dichiarerebbe vuoto un ambito che vuoto non è (spec FR-009, decisione #16).
export type RuoloConteggio = 'pulsante' | 'etichettaAmbito';

// Conteggio della VARIANTE nel dialogo di variante: gli unici due numeri che quel dialogo può
// promettere sono i suoi, mai quelli dell'articolo che arrivano nella stessa risposta (FR-016).
export interface ConteggioVariante {
  righeInCorso: number;
  righeTotali: number;
}

// Campi del blocco articolo che si applicano con il PREDICATO PIENO. `VarianteArt` non è fra questi:
// dal blocco articolo tocca le sole righe senza variante (research D6, spec FR-022).
const CAMPI_A_PREDICATO_PIENO = ['CodiceArticolo', 'UdM', 'UdM2', 'MagUdM'];

/**
 * Numero di righe che la selezione corrente aggiornerebbe DAVVERO, in funzione dei campi spuntati
 * oltre che dell'ambito. È la regola che SC-002 verifica, e sta QUI e non nel componente perché
 * pulsante ed etichette d'ambito non possano divergere.
 *
 * Ritorna `null` quando non c'è un numero da mostrare: conteggio non disponibile (fail-open di
 * FR-012 — la conferma resta ABILITATA) oppure, per il solo pulsante, nessun campo spuntato.
 */
export function conteggioRigheDaAggiornare(
  selezionate: string[],
  ambito: AmbitoPropagazione,
  impatto: AnteprimaImpattoOrdiniArticolo | null | undefined,
  ruolo: RuoloConteggio = 'pulsante',
  variante?: ConteggioVariante | null,
): number | null {
  const conteggi = impatto?.disponibile ? impatto.articolo : null;
  if (!conteggi) return null;

  // Dialogo di VARIANTE: i conteggi sono quelli di quella variante, in entrambi i ruoli.
  if (variante) return ambito === 'inCorso' ? variante.righeInCorso : variante.righeTotali;

  if (selezionate.length === 0) {
    // Il pulsante resta senza numero e disabilitato; le etichette mostrano il conteggio d'ambito.
    return ruolo === 'pulsante' ? null : conteggioDiAmbito(conteggi, ambito);
  }

  // Almeno un campo a predicato pieno ⇒ tutto l'ambito. Altrimenti l'unico campo spuntato del blocco
  // articolo è il codice variante, che aggiorna le sole righe senza variante.
  if (selezionate.some(chiave => CAMPI_A_PREDICATO_PIENO.includes(chiave)))
    return conteggioDiAmbito(conteggi, ambito);
  return ambito === 'inCorso'
    ? conteggi.righeSenzaVarianteInCorso
    : conteggi.righeSenzaVarianteTotali;
}

function conteggioDiAmbito(conteggi: ImpattoConteggioArticolo, ambito: AmbitoPropagazione): number {
  return ambito === 'inCorso' ? conteggi.righeInCorso : conteggi.righeTotali;
}
